const EPSILON = 0.0001;
const M2_SNAP_DEBOUNCE_MS = 1000;
const WASTAGE_MIN_SNAP_DEBOUNCE_MS = 500;

class TileCalculator {
  constructor(config) {
    this.config = config;
  }

  roundArea(value) {
    return Math.round(value * 100) / 100;
  }

  formatArea(value) {
    return this.roundArea(value).toString();
  }

  getM2PerBox() {
    const { tilesPerBox, tilesPerM2 } = this.config;
    if (tilesPerBox <= 0 || tilesPerM2 <= 0) return 0;
    return Math.floor((tilesPerBox * 1000) / tilesPerM2) / 1000;
  }

  getNearestBoxCountFromM2(m2) {
    const m2PerBox = this.getM2PerBox();
    if (m2PerBox <= 0) return 1;
    return Math.max(1, Math.round(m2 / m2PerBox));
  }

  snapM2ToNearestInterval(m2) {
    if (!this.config.roundToBox) return m2;
    const m2PerBox = this.getM2PerBox();
    if (m2PerBox <= 0) return m2;
    const minM2 = this.roundArea(m2PerBox);
    if (m2 <= 0) return minM2;
    const boxCount = this.getNearestBoxCountFromM2(m2);
    return this.roundArea(boxCount * m2PerBox);
  }

  getDisplayM2FromTiles(totalTiles) {
    const { roundToBox, tilesPerBox, tilesPerM2 } = this.config;
    if (!roundToBox || tilesPerBox <= 1) {
      return this.roundArea(totalTiles / tilesPerM2);
    }
    const boxCount = Math.ceil(totalTiles / tilesPerBox);
    return this.roundArea(boxCount * this.getM2PerBox());
  }

  getTilesFromArea(m2) {
    const { tilesPerM2, roundToBox, tilesPerBox } = this.config;

    if (roundToBox && tilesPerBox > 1) {
      return this.getNearestBoxCountFromM2(m2) * tilesPerBox;
    }

    return Math.ceil(m2 * tilesPerM2 - EPSILON);
  }

  roundUpToBoxMultiple(quantity) {
    if (!this.config.roundToBox) return quantity;
    return Math.ceil(quantity / this.config.tilesPerBox) * this.config.tilesPerBox;
  }

  getBoxCountFromTiles(totalTiles) {
    const { tilesPerBox } = this.config;
    if (tilesPerBox <= 0) return 1;
    return Math.max(1, Math.ceil(totalTiles / tilesPerBox));
  }

  getTilesFromBoxCount(boxCount) {
    return boxCount * this.config.tilesPerBox;
  }

  applyWastageToBoxCount(boxCount, multiplier, enabled) {
    if (!enabled) {
      return Math.max(1, Math.floor(boxCount / multiplier + EPSILON));
    }
    return Math.max(1, Math.ceil(boxCount * multiplier - EPSILON));
  }
}

class ProductDetailsForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.el = {};
    const selectors = {
      areaInput: '[data-m2-input]',
      qtyHidden: '[data-quantity-input]',
      qtyDisplay: '[data-quantity-value]',
      qtyNumber: '[data-quantity-number]',
      qtyUnit: '[data-quantity-unit]',
      wastage: '[data-wastage-input]',
      totalPriceEl: '[data-total-price]',
      totalQuantityEl: '[data-total-quantity]',
      toggleWrapper: '[data-calculator-toggle-wrapper]',
      calculatorPanel: '[data-surface-calculator]',
      mainQuantityWrapper: '[data-main-quantity-wrapper]',
      stockMsg: '[data-stock-message]',
      surfacesContainer: '[data-surfaces-container]',
      surfaceTotal: '[data-surface-total-value]',
      addSurface: '[data-add-surface]',
      qtyMinus: '[data-quantity-minus]',
      qtyPlus: '[data-quantity-plus]',
      m2Minus: '[data-m2-minus]',
      m2Plus: '[data-m2-plus]',
    };
    for (const [k, s] of Object.entries(selectors)) this.el[k] = this.querySelector(s);

    const d = this.form.dataset;
    this.conf = {
      tilesPerBox: Number.parseInt(d.tilesPerBox, 10) || 1,
      tilesPerM2: Number.parseFloat(d.tilesPerM2) || 1,
      price: Number.parseFloat(d.variantPrice) || 0,
      format: d.moneyFormat || '£{{amount}}',
      soldAsBox: d.soldAsBox === 'true',
      qtyStep: Number.parseInt(d.qtyStep, 10) || 1,
      roundToBox: d.roundToBox === 'true',
      inventoryPolicy: d.inventoryPolicy || 'continue',
      inventoryQuantity: Number.parseInt(d.inventoryQuantity, 10) || 0,
    };

    this.calc = new TileCalculator({
      tilesPerBox: this.conf.tilesPerBox,
      tilesPerM2: this.conf.tilesPerM2,
      roundToBox: this.conf.roundToBox,
      qtyStep: this.conf.qtyStep,
    });

    this.labels = {
      unitSingular: this.dataset.unitSingular || 'Tile',
      unitPlural: this.dataset.unitPlural || 'Tiles',
      boxSingular: this.dataset.boxSingular || 'Box',
      boxPlural: this.dataset.boxPlural || 'Boxes',
      stockUnavailable: this.dataset.stockUnavailable || 'Not enough stock available.',
      stockLimited: this.dataset.stockLimited || 'Not enough stock available. Only {{ count }} in stock.',
    };

    this.showBoxesMessaging = this.dataset.showBoxesMessaging === 'true';

    this.surfaceLabels = {
      width: this.dataset.surfaceWidthLabel || 'Surface __NUMBER__ Width',
      height: this.dataset.surfaceHeightLabel || 'Surface __NUMBER__ Height',
      remove: this.dataset.removeSurfaceLabel || 'Remove surface __NUMBER__',
      widthAria: this.dataset.surfaceWidthAria || 'Surface __NUMBER__ width in meters',
      heightAria: this.dataset.surfaceHeightAria || 'Surface __NUMBER__ height in meters',
    };

    this.hasCalculator = !!this.el.areaInput;
    this.isSyncing = false;
    this.hasPendingM2Snap = false;
    this.hasPendingWastageSnap = false;

    if (this.el.wastage) {
      this.el.wastage.checked = true;
    }

    this.attachEvents();

    if (this.hasCalculator) {
      this.syncFromBoxCount(this.getMinBoxCount());
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onToggleClick);
    this.el.surfacesContainer?.removeEventListener('input', this.onSurfaceInput);
    this.el.surfacesContainer?.removeEventListener('click', this.onSurfaceClick);
    this.el.addSurface?.removeEventListener('click', this.onAddSurface);
    this.el.qtyMinus?.removeEventListener('click', this.onMinusClick);
    this.el.qtyPlus?.removeEventListener('click', this.onPlusClick);
    this.el.m2Minus?.removeEventListener('click', this.onM2MinusClick);
    this.el.m2Plus?.removeEventListener('click', this.onM2PlusClick);
    this.el.wastage?.removeEventListener('change', this.onWastageChange);
    this.el.areaInput?.removeEventListener('input', this.onM2Input);
    this.el.areaInput?.removeEventListener('blur', this.onM2Blur);
    clearTimeout(this.m2DebounceTimer);
    this.clearWastageMinSnap();
    this.form?.removeEventListener('submit', this.onSubmit);
  }

  getUnit(qty) {
    return qty === 1 ? this.labels.unitSingular : this.labels.unitPlural;
  }

  getWastageMultiplier() {
    const wastagePercent = Number.parseInt(this.el.wastage?.value, 10) || 10;
    return 1 + wastagePercent / 100;
  }

  getMinBoxCount() {
    const baseMin = 1;
    if (!this.el.wastage?.checked) return baseMin;
    return this.calc.applyWastageToBoxCount(baseMin, this.getWastageMultiplier(), true);
  }

  getMinQuantity() {
    const { soldAsBox, tilesPerBox } = this.conf;
    const minBoxCount = this.getMinBoxCount();
    if (soldAsBox) return minBoxCount;
    return minBoxCount * tilesPerBox;
  }

  getBaseMinBoxCount() {
    return 1;
  }

  getBaseMinQuantity() {
    const { soldAsBox, tilesPerBox } = this.conf;
    const baseMin = this.getBaseMinBoxCount();
    if (soldAsBox) return baseMin;
    return baseMin * tilesPerBox;
  }

  scheduleWastageMinSnap() {
    this.clearWastageMinSnap();
    this.hasPendingWastageSnap = true;
    this.wastageSnapTimer = setTimeout(() => this.applyWastageMinSnap(), WASTAGE_MIN_SNAP_DEBOUNCE_MS);
  }

  applyWastageMinSnap() {
    this.hasPendingWastageSnap = false;
    this.syncFromBoxCount(this.getMinBoxCount());
  }

  clearWastageMinSnap() {
    clearTimeout(this.wastageSnapTimer);
    this.hasPendingWastageSnap = false;
  }

  getCurrentBoxCount() {
    const { tilesPerBox, soldAsBox } = this.conf;
    const qtyVal = Number.parseInt(this.el.qtyHidden.value, 10) || 1;
    const totalTiles = soldAsBox ? qtyVal * tilesPerBox : qtyVal;
    return this.calc.getBoxCountFromTiles(totalTiles);
  }

  syncFromBoxCount(boxCount) {
    const { soldAsBox } = this.conf;
    const totalTiles = this.calc.getTilesFromBoxCount(boxCount);
    const displayM2 = this.calc.formatArea(this.calc.getDisplayM2FromTiles(totalTiles));
    const qtyForInput = soldAsBox ? boxCount : totalTiles;

    this.el.qtyHidden.value = qtyForInput;
    this.setAreaValue(displayM2);
    this.displayTotals(totalTiles, displayM2);
    this.checkStockStatus(totalTiles);
  }

  sanitizeNumericInput(e) {
    e.target.value = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.');
  }

  applyM2SnapAndCalc() {
    this.hasPendingM2Snap = false;
    this.recalcFromArea();
  }

  setAreaValue(value) {
    if (!this.el.areaInput) return;
    this.isSyncing = true;
    this.el.areaInput.value = value;
    this.isSyncing = false;
  }

  formatSurfaceLabel(template, number) {
    return template.replace(/__NUMBER__/g, String(number));
  }

  attachEvents() {
    this.onToggleClick = (e) => {
      if (!e.target.closest('[data-calculator-toggle]')) return;
      const active = this.el.calculatorPanel.toggleAttribute('data-active');
      this.el.mainQuantityWrapper?.toggleAttribute('hidden', active);
      this.el.toggleWrapper?.toggleAttribute('hidden', active);
      this.querySelectorAll('[data-calculator-toggle]').forEach((btn) => {
        btn.setAttribute('aria-expanded', String(active));
      });
    };
    this.addEventListener('click', this.onToggleClick);

    this.bindSurfaceRows();
    this.updateSurfaceRows();

    this.onAddSurface = () => this.addSurfaceRow();
    this.el.addSurface?.addEventListener('click', this.onAddSurface);

    this.onMinusClick = () => this.stepQty(-1);
    this.onPlusClick = () => this.stepQty(1);
    this.onM2MinusClick = () => this.stepM2(-1);
    this.onM2PlusClick = () => this.stepM2(1);
    this.onWastageChange = () => {
      clearTimeout(this.m2DebounceTimer);
      this.hasPendingM2Snap = false;
      this.clearWastageMinSnap();
      const boxCount = this.getCurrentBoxCount();
      const newBoxCount = this.calc.applyWastageToBoxCount(
        boxCount,
        this.getWastageMultiplier(),
        this.el.wastage?.checked,
      );
      this.syncFromBoxCount(newBoxCount);
    };
    this.onM2Input = (e) => {
      if (this.isSyncing) return;
      this.sanitizeNumericInput(e);
      this.hasPendingM2Snap = true;
      clearTimeout(this.m2DebounceTimer);
      this.m2DebounceTimer = setTimeout(() => this.applyM2SnapAndCalc(), M2_SNAP_DEBOUNCE_MS);
    };
    this.onM2Blur = (e) => {
      if (this.isSyncing) return;
      this.sanitizeNumericInput(e);
      if (!this.hasPendingM2Snap) return;
      clearTimeout(this.m2DebounceTimer);
      this.applyM2SnapAndCalc();
    };
    this.onSubmit = (e) => this.handleSubmit(e);

    this.el.qtyMinus?.addEventListener('click', this.onMinusClick);
    this.el.qtyPlus?.addEventListener('click', this.onPlusClick);
    this.el.m2Minus?.addEventListener('click', this.onM2MinusClick);
    this.el.m2Plus?.addEventListener('click', this.onM2PlusClick);
    this.el.wastage?.addEventListener('change', this.onWastageChange);
    this.el.areaInput?.addEventListener('input', this.onM2Input);
    this.el.areaInput?.addEventListener('blur', this.onM2Blur);
    this.form.addEventListener('submit', this.onSubmit);
  }

  bindSurfaceRows() {
    this.onSurfaceInput = (e) => {
      if (e.target.matches('[data-surface-width], [data-surface-height]')) {
        this.sanitizeNumericInput(e);
        this.calcSurfaces();
      }
    };
    this.onSurfaceClick = (e) => {
      const removeBtn = e.target.closest('[data-remove-surface]');
      if (!removeBtn) return;
      const row = removeBtn.closest('[data-surface-row]');
      if (row) this.removeSurfaceRow(row);
    };
    this.el.surfacesContainer?.addEventListener('input', this.onSurfaceInput);
    this.el.surfacesContainer?.addEventListener('click', this.onSurfaceClick);
  }

  updateSurfaceRows() {
    const rows = this.el.surfacesContainer?.querySelectorAll('[data-surface-row]') ?? [];
    const { width, height, remove, widthAria, heightAria } = this.surfaceLabels;
    const idPrefix = `surface-${this.dataset.sectionId || 'product'}`;

    rows.forEach((row, index) => {
      const number = index + 1;
      const widthLabel = row.querySelector('[data-surface-width-label]');
      const heightLabel = row.querySelector('[data-surface-height-label]');
      const widthInput = row.querySelector('[data-surface-width]');
      const heightInput = row.querySelector('[data-surface-height]');
      const removeBtn = row.querySelector('[data-remove-surface]');

      if (widthLabel) {
        widthLabel.textContent = this.formatSurfaceLabel(width, number);
        widthLabel.htmlFor = `${idPrefix}-${number}-width`;
      }
      if (heightLabel) {
        heightLabel.textContent = this.formatSurfaceLabel(height, number);
        heightLabel.htmlFor = `${idPrefix}-${number}-height`;
      }
      if (widthInput) {
        widthInput.id = `${idPrefix}-${number}-width`;
        widthInput.setAttribute('aria-label', this.formatSurfaceLabel(widthAria, number));
      }
      if (heightInput) {
        heightInput.id = `${idPrefix}-${number}-height`;
        heightInput.setAttribute('aria-label', this.formatSurfaceLabel(heightAria, number));
      }
      if (removeBtn) {
        removeBtn.hidden = index === 0;
        removeBtn.setAttribute('aria-label', this.formatSurfaceLabel(remove, number));
      }
    });
  }

  removeSurfaceRow(row) {
    const rows = this.el.surfacesContainer?.querySelectorAll('[data-surface-row]') ?? [];
    if (rows.length <= 1) return;
    row.remove();
    this.updateSurfaceRows();
    this.calcSurfaces();
  }

  calcSurfaces() {
    const rows = this.el.surfacesContainer?.querySelectorAll('[data-surface-row]') ?? [];
    let totalArea = 0;
    for (const row of rows) {
      const w = Number.parseFloat(row.querySelector('[data-surface-width]')?.value) || 0;
      const h = Number.parseFloat(row.querySelector('[data-surface-height]')?.value) || 0;
      totalArea += w * h;
    }

    if (totalArea <= 0) {
      if (this.el.surfaceTotal) this.el.surfaceTotal.textContent = '0m²';
      return;
    }

    if (this.el.surfaceTotal) this.el.surfaceTotal.textContent = `${this.calc.formatArea(totalArea)}m²`;
    this.setAreaValue(this.calc.formatArea(totalArea));
    this.recalcFromArea();
  }

  addSurfaceRow() {
    const template = this.el.surfacesContainer?.querySelector('[data-surface-row]');
    if (!template) return;
    const clone = template.cloneNode(true);
    clone.querySelectorAll('input').forEach((i) => {
      i.value = '';
    });
    this.el.surfacesContainer.appendChild(clone);
    this.updateSurfaceRows();
  }

  recalcFromArea() {
    const { qtyStep, roundToBox } = this.conf;
    let areaM2 = Number.parseFloat(this.el.areaInput?.value) || 0;

    if (this.el.wastage?.checked) {
      areaM2 *= this.getWastageMultiplier();
    }

    if (roundToBox) {
      areaM2 = this.calc.snapM2ToNearestInterval(areaM2);
    }

    let totalTiles = this.calc.getTilesFromArea(areaM2);

    if (this.conf.tilesPerBox > 1 && !roundToBox) {
      totalTiles = Math.ceil(totalTiles / this.conf.tilesPerBox) * this.conf.tilesPerBox;
    }

    totalTiles = Math.max(qtyStep, totalTiles);
    this.syncFromBoxCount(this.calc.getBoxCountFromTiles(totalTiles));
  }

  recalcFromQuantity() {
    const { tilesPerBox, soldAsBox } = this.conf;
    const inputVal = Number.parseInt(this.el.qtyHidden.value, 10) || 1;
    let totalTiles = soldAsBox ? inputVal * tilesPerBox : inputVal;

    totalTiles = this.calc.roundUpToBoxMultiple(totalTiles);
    this.syncFromBoxCount(this.calc.getBoxCountFromTiles(totalTiles));
  }

  stepQty(direction) {
    const { qtyStep, tilesPerBox, soldAsBox } = this.conf;
    const wastageMinQty = this.hasCalculator ? this.getMinQuantity() : qtyStep;
    const floorQty =
      direction < 0 && this.hasCalculator && this.el.wastage?.checked
        ? this.getBaseMinQuantity()
        : wastageMinQty;
    const current = Number.parseInt(this.el.qtyHidden.value, 10) || wastageMinQty;
    const next = Math.max(floorQty, current + direction * qtyStep);
    this.el.qtyHidden.value = next;

    if (this.hasCalculator) {
      clearTimeout(this.m2DebounceTimer);
      this.hasPendingM2Snap = false;

      if (this.el.wastage?.checked && next < wastageMinQty) {
        const totalTiles = soldAsBox ? next * tilesPerBox : next;
        this.syncFromBoxCount(this.calc.getBoxCountFromTiles(totalTiles));
        this.scheduleWastageMinSnap();
      } else {
        this.clearWastageMinSnap();
        this.recalcFromQuantity();
      }
    } else {
      const totalTiles = soldAsBox ? next * tilesPerBox : next;
      this.displayTotals(totalTiles, '0');
      this.checkStockStatus(totalTiles);
    }
  }

  stepM2(direction) {
    if (!this.el.areaInput) return;

    clearTimeout(this.m2DebounceTimer);
    this.hasPendingM2Snap = false;

    const floor =
      direction < 0 && this.el.wastage?.checked ? this.getBaseMinBoxCount() : this.getMinBoxCount();
    const nextBoxCount = Math.max(floor, this.getCurrentBoxCount() + direction);
    this.syncFromBoxCount(nextBoxCount);

    if (this.el.wastage?.checked && nextBoxCount < this.getMinBoxCount()) {
      this.scheduleWastageMinSnap();
    } else {
      this.clearWastageMinSnap();
    }
  }

  displayTotals(totalTiles, totalArea) {
    const { tilesPerBox, soldAsBox, price, format } = this.conf;
    const totalBoxes = Math.ceil(totalTiles / tilesPerBox);
    const displayQty = soldAsBox ? totalBoxes : totalTiles;
    const totalPrice = soldAsBox ? ((totalBoxes * price) / 100).toFixed(2) : ((totalTiles * price) / 100).toFixed(2);

    document.dispatchEvent(
      new CustomEvent('passTotalTilesToProductUpsellJs', {
        detail: { totalTiles },
      }),
    );

    if (this.el.qtyNumber) this.el.qtyNumber.textContent = displayQty;
    if (this.el.qtyUnit) this.el.qtyUnit.textContent = this.getUnit(displayQty);
    if (this.el.totalQuantityEl) {
      let boxDetail = '';
      if (this.showBoxesMessaging) {
        const boxLabel = totalBoxes === 1 ? this.labels.boxSingular : this.labels.boxPlural;
        boxDetail = ` (${totalBoxes} ${boxLabel})`;
      }
      this.el.totalQuantityEl.textContent = `${displayQty} ${this.getUnit(displayQty)}${boxDetail} / ${totalArea}m²`;
    }
    if (this.el.totalPriceEl) {
      this.el.totalPriceEl.innerHTML = format.replace(/\{\{\s*amount\s*\}\}/, totalPrice);
    }
  }

  /**
   * Check inventory and show stock message if needed.
   */
  checkStockStatus(currentQty) {
    const { inventoryPolicy, inventoryQuantity, soldAsBox, tilesPerBox } = this.conf;
    const msgEl = this.el.stockMsg;
    const submitBtn = this.form.querySelector('[type="submit"]');

    let errorMessage = '';

    if (inventoryPolicy === 'deny') {
      const minStock = soldAsBox ? tilesPerBox : 1;
      if (inventoryQuantity < minStock) {
        errorMessage = this.labels.stockUnavailable;
      } else if (currentQty > inventoryQuantity) {
        errorMessage = this.labels.stockLimited.replace('{{ count }}', inventoryQuantity);
      }
    }

    if (msgEl) {
      msgEl.textContent = errorMessage;
      msgEl.hidden = !errorMessage;
    }
    if (submitBtn) {
      submitBtn.disabled = !!errorMessage;
    }
  }

  async handleSubmit(e) {
    e.preventDefault();
    const btn = this.form.querySelector('[type="submit"]');
    if (!btn) return;

    if (this.hasPendingWastageSnap) {
      clearTimeout(this.wastageSnapTimer);
      this.applyWastageMinSnap();
    }

    btn.disabled = true;
    btn.classList.add('btn--loading');

    try {
      const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: new FormData(this.form),
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || this.labels.stockUnavailable);

      const cartRes = await fetch(window.Shopify.routes.root + 'cart.js', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      const cart = cartRes.ok ? await cartRes.json() : null;

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: data, addToCartBtn: btn } }));
    } catch (err) {
      console.error(err);

      if (this.el.stockMsg) {
        this.el.stockMsg.textContent = err.message;
        this.el.stockMsg.hidden = false;
      }
    } finally {
      btn.disabled = false;
      btn.classList.remove('btn--loading');
    }
  }
}

if (!customElements.get('product-details-form')) {
  customElements.define('product-details-form', ProductDetailsForm);
}
